/**
 * The FitLife-X issue list.
 *
 * Add an issue here and every rendering follows from it: the LinkedIn article
 * text, the Mailchimp HTML, the Outlook HTML, the Resend send, and the
 * article page on the site. Nothing else needs editing.
 *
 * Conventions:
 * - `number` increments by one. It appears in the email standfirst.
 * - `publishOn` is the Monday the issue goes out, as YYYY-MM-DD text.
 * - `slug` becomes /insights/<slug> on the site, so it never changes once an
 *   issue has been published - a changed slug is a dead link in every email
 *   already sent.
 * - `linkedInLabel` carries the emoji-and-caps section label used in the
 *   LinkedIn posts; `heading` is the sentence-case version the site and the
 *   email use.
 *
 * The October arc runs to January: plan the demand, staff for it, then keep
 * the people it brings. One argument across three weeks rather than three
 * unrelated posts.
 */

import type { NewsletterIssue } from "./newsletter";

export const newsletterIssues: NewsletterIssue[] = [
  {
    number: 1,
    publishOn: "2026-10-12",
    slug: "your-january-is-decided-in-october",
    category: "Operations",
    title: "Your January Is Decided in October.",
    deck: "The gyms that handle the New Year well are the ones that finished planning for it before Thanksgiving.",
    metaTitle: "Your January Is Decided in October | Altitude Fitness",
    metaDescription:
      "Independent gym operators should plan January capacity, class schedules and onboarding in October, while there is still time to change anything.",
    readTime: "5 min read",
    intro:
      "Every operator knows January is coming. Almost nobody plans for it while there is still time to do anything about it.",
    sections: [
      {
        heading: "January is a capacity problem before it is a sales problem",
        linkedInLabel: "📊 IT IS A CAPACITY PROBLEM FIRST",
        paragraphs: [
          "The New Year does not bring you a steady trickle of new members. It brings a wave into the same floor space, the same class slots, the same handful of coaches, in the same three weeks. Most of what goes wrong in January is not a failure of marketing. It is a room that was already full at 6pm in November.",
          "So start with the constraint, not the campaign. How many people can actually be in your busiest class? How many intro sessions can your team deliver in a week without cancelling on existing clients? Write those numbers down before you commit to a single promotion. A January offer that outruns your capacity does not grow the business — it spends your reputation to sell memberships you cannot serve.",
        ],
      },
      {
        heading: "Last January is the only forecast you need",
        linkedInLabel: "📈 LAST JANUARY IS YOUR FORECAST",
        paragraphs: [
          "You do not need an industry report. You have your own data, and it is better. Pull last January day by day: joins, visits by hour, class fill rates, intro sessions booked, cancellations in February and March. The shape of that month is the shape of the next one, give or take what you have changed since.",
          "Three questions answer most of it. Which days and hours actually broke? Which classes turned people away? And how many of the people who joined that January were still with you in April? That last number is the one worth staring at, because it tells you whether January is a growth month for your business or an expensive churn cycle you repeat annually.",
        ],
      },
      {
        heading: "Decide now what you will not do",
        linkedInLabel: "✂️ DECIDE WHAT YOU WON'T DO",
        paragraphs: [
          "October is when you still have the authority to say no cheaply. In December every decision is a scramble and in January every decision is an apology. Lock the class schedule, the staffing plan and the promotion before the holidays, and put the deadline in writing so it does not quietly move.",
          "That includes deciding what you will not sell. If your 6pm strength class is the thing that breaks first, do not build a campaign that drives everyone to 6pm. Point the offer at the capacity you have — midday, early morning, the room that sits empty on Saturdays — and you convert the same demand into members you can actually serve.",
        ],
      },
      {
        heading: "Set the measures before the month, not after it",
        linkedInLabel: "🎯 SET THE MEASURES NOW",
        paragraphs: [
          "Agree now how you will know whether January worked, because in February everyone remembers it the way that suits them. A short, honest scorecard: joins, intro sessions actually delivered, class fill against capacity, 30-day and 90-day retention of the January cohort, and staff hours against plan.",
          "Give each number an owner and a weekly review slot through the month. Reviewing it in March means finding out what went wrong after you can no longer fix it. Reviewing it on the second Monday of January means you still have three weeks of the rush left to change something.",
        ],
      },
    ],
    takeaways: [
      "Write down your real capacity: busiest class, busiest hour, intro sessions per week",
      "Pull last January day by day — joins, visits by hour, class fill, and who was still there in April",
      "Lock the January schedule, staffing plan and offer before the holidays",
      "Point the promotion at the hours you can actually serve",
      "Agree the January scorecard and its weekly review now",
    ],
    closing:
      "If you are going into another January hoping it works out, that is the part we fix. Altitude builds the operating plan — capacity, staffing, onboarding and the numbers that tell you it is working — so the New Year is a month you run instead of a month that happens to you. A fifteen-minute call is enough to see whether we can help.",
    sources: [
      {
        title: "Plan Your Business — U.S. Small Business Administration",
        href: "https://www.sba.gov/counseling/plan-your-business/",
      },
      {
        title: "Why Health Club Retention Requires a Technology Solution — Health & Fitness Association",
        href: "https://www.healthandfitness.org/why-health-club-retention-requires-a-technology-solution/",
      },
    ],
    tags: ["FitnessBusiness", "GymOwners", "StudioManagement", "FitnessIndustry", "GymOperations"],
  },

  {
    number: 2,
    publishOn: "2026-10-19",
    slug: "staffing-for-january-starts-now",
    category: "Operations",
    title: "You Cannot Hire Your Way Out of January in January.",
    deck: "Hiring, onboarding and scheduling for the New Year all have lead times, and all of them are longer than you think.",
    metaTitle: "Staffing for the January Rush | Altitude Fitness",
    metaDescription:
      "Why gym and studio staffing for January has to be decided in the autumn: hiring lead times, onboarding, schedules and holiday coverage.",
    readTime: "5 min read",
    intro:
      "The January plan most operators have is a hope that their best people will absorb it. They usually can. That is exactly the problem.",
    sections: [
      {
        heading: "Hiring has a lead time, and it runs through the holidays",
        linkedInLabel: "⏱️ HIRING HAS A LEAD TIME",
        paragraphs: [
          "Posting a role, screening, interviewing, checking credentials, onboarding and getting someone genuinely useful on your floor is not a two-week process. Run it in December and you are interviewing during the worst hiring month of the year, then putting an unprepared new coach in front of your newest members in your busiest week.",
          "Work backwards from the first week of January instead. If a new trainer needs three weeks to be useful, and hiring takes four to six, you are posting in late October. That is the whole argument for doing this now rather than after Thanksgiving.",
        ],
      },
      {
        heading: "Decide who owns each shift, not just who is on it",
        linkedInLabel: "👥 OWNERSHIP, NOT JUST COVERAGE",
        paragraphs: [
          "A January schedule with enough names on it can still fall apart, because a busy floor does not need another body so much as it needs someone accountable. Name the person on every shift who owns the opening checks, the escalation when something breaks, the incident log and the handoff at the end.",
          "That person is the difference between a problem being handled and a problem being described to you later. It also protects your best coaches: without a shift owner, the most conscientious person on the floor silently becomes one, and that is how you lose them by March.",
        ],
      },
      {
        heading: "Cross-train before you need it, not during",
        linkedInLabel: "🔁 CROSS-TRAIN BEFORE YOU NEED IT",
        paragraphs: [
          "Map the skills your January actually requires: front desk and check-in under load, intro sessions, class coverage, equipment faults, difficult billing conversations, emergency procedures. Then look at how many people can do each one. Anywhere the answer is one, you have a single point of failure walking around with a phone that can call in sick.",
          "November and December are when you fix that cheaply, in quiet hours, with your experienced staff still calm enough to teach. In January the same training happens under pressure, badly, in front of new members.",
        ],
      },
      {
        heading: "Protect the people who will carry it",
        linkedInLabel: "🛡️ PROTECT THE PEOPLE CARRYING IT",
        paragraphs: [
          "Publish the January schedule early and honour the time off you agreed. A team that finds out about its January in late December is a team that starts the year already resentful, and members feel that within a week.",
          "Decide in advance what gets dropped when the month is heavier than planned — which admin waits, which meetings are cancelled, which extras pause. Deciding that now is leadership. Deciding it at 6pm on a Tuesday in January is triage, and your staff will remember which one they got.",
        ],
      },
    ],
    takeaways: [
      "Work backwards from week one of January: post roles now if hiring takes four to six weeks",
      "Name a shift owner for every January shift, not just a headcount",
      "Map the skills January needs and fix every single point of failure before December",
      "Publish the January schedule early and honour the agreed time off",
      "Decide in advance what gets dropped on the heaviest weeks",
    ],
    closing:
      "Most owners did not get into this industry to run hiring pipelines, comp plans and shift rosters. That is the part we take on — building the staffing plan, the onboarding and the standards that let your coaches do the work they are good at. If your January depends on two people not getting sick, let's talk.",
    sources: [
      {
        title: "Fitness Trainers and Instructors — U.S. Bureau of Labor Statistics",
        href: "https://www.bls.gov/ooh/personal-care-and-service/fitness-trainers-and-instructors.htm",
      },
      {
        title: "Recommended Practices for Safety and Health Programs — OSHA",
        href: "https://www.osha.gov/safety-management",
      },
    ],
    tags: ["FitnessBusiness", "GymOwners", "StudioManagement", "Leadership", "FitnessIndustry"],
  },

  {
    number: 3,
    publishOn: "2026-10-26",
    slug: "the-january-joiner-problem",
    category: "Retention",
    title: "January Joiners Don't Quit in March. They Quit in Week Two.",
    deck: "The New Year cohort is won or lost in the first fortnight, and almost every operator looks at it a quarter too late.",
    metaTitle: "Keeping January Joiners | Altitude Fitness",
    metaDescription:
      "How independent gyms keep the January cohort: onboarding in the first two weeks, early warning signs, and the check-in that is worth making.",
    readTime: "6 min read",
    intro:
      "Everyone blames March. By March the decision was made weeks earlier, by someone who walked in on a Tuesday, could not get on a rack, did not speak to anyone, and quietly stopped coming.",
    sections: [
      {
        heading: "The first two weeks are the whole decision",
        linkedInLabel: "🗓️ THE FIRST TWO WEEKS DECIDE IT",
        paragraphs: [
          "A new member arrives with a decision already half-made: whether this is a place where they belong. Two weeks of being unnoticed settles it. So does one week of being welcomed by name, shown how the room works, and given something specific to do on their next three visits.",
          "That is an operations question, not a motivational one. Who greets them. What happens in the first session. What they are told to do on day three when nobody is scheduled to be with them. Write that journey down and it happens the same way whoever is on shift. Leave it to goodwill and it happens for the members who happen to be confident enough to ask.",
        ],
      },
      {
        heading: "Give them one early win they can feel",
        linkedInLabel: "🏆 GIVE THEM AN EARLY WIN",
        paragraphs: [
          "Weight on a scale is a slow, discouraging first measure. Give a new member something that moves in two weeks: four sessions attended, a lift performed properly, a class series finished, a movement that stopped hurting. Name it in the first session and acknowledge it when it happens.",
          "This is also what makes your coaching visible. A member who can describe what changed is a member who can explain to themselves why they are paying you, and that explanation is what survives the first cold February morning.",
        ],
      },
      {
        heading: "Attendance is your early warning system",
        linkedInLabel: "⚠️ ATTENDANCE IS THE EARLY WARNING",
        paragraphs: [
          "The signal you need is already in your system. A January joiner who came four times in week one and not at all in week three has told you something a cancellation form never will. Decide now what the trigger is, who sees it, and what they do about it.",
          "What they do matters as much as that they do it. A useful outreach asks what got in the way — the schedule, the confidence, the programme, the commute — and offers a concrete alternative. A sales message dressed as concern reads as exactly what it is and costs you the second chance.",
        ],
      },
      {
        heading: "Record why people leave, and act on the pattern",
        linkedInLabel: "📋 RECORD WHY THEY LEAVE",
        paragraphs: [
          "Cancellation reasons collected properly are the cheapest research you will ever get. Use a short, consistent set of options, record them at the moment of cancellation, and review them by cohort, by coach, by class and by how the member joined in the first place.",
          "Then act on what it shows, including when it says something inconvenient — that a promotion is bringing in people your programme does not fit, or that one time slot is where everyone gives up. Retention work is supposed to improve the experience, not to pressure people into staying in a membership that was wrong for them.",
        ],
      },
    ],
    takeaways: [
      "Write the first-two-weeks journey down: greeting, first session, and what they do on day three",
      "Name one early win in the first session and acknowledge it when it lands",
      "Set an attendance trigger now, with an owner and a response that is not a sales message",
      "Collect cancellation reasons consistently and review them by cohort, coach and time slot",
      "Measure the January cohort at 30 and 90 days, not at the end of the quarter",
    ],
    closing:
      "A January that fills the room and empties it by spring is not growth — it is an expensive habit. Altitude builds the onboarding, the early-warning triggers and the retention rhythm that turn a New Year cohort into members who are still there in June. If you want next January to be the one that sticks, let's talk.",
    sources: [
      {
        title: "Motives and Barriers to Exercise Adherence in a Fitness Club Setting",
        href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7497044/",
      },
      {
        title: "Adult Physical Activity Basics — CDC",
        href: "https://www.cdc.gov/physical-activity-basics/guidelines/adults.html",
      },
      {
        title: "Why Health Club Retention Requires a Technology Solution — Health & Fitness Association",
        href: "https://www.healthandfitness.org/why-health-club-retention-requires-a-technology-solution/",
      },
    ],
    tags: ["FitnessBusiness", "GymOwners", "MemberRetention", "StudioManagement", "FitnessIndustry"],
  },
];

/** Issues are keyed by slug everywhere outside this file. */
export const issueBySlug = (slug: string): NewsletterIssue | undefined =>
  newsletterIssues.find(issue => issue.slug === slug);
